/**
 * SpaceVrCockpit — the SECTOR's cockpit in a headset (space campaign L, 2026-10-01; gap 27, report §10.1 last ¶).
 *
 * WHY IT EXISTS. three's XRManager replaces the rig-child camera's local pose with the head pose every render, so the
 * sector's chase / cockpit eye offset never reaches a headset: the head sits at the rig origin (the hull's centre) — and
 * the 2-D canopy (`CockpitOverlay`) and the whole sector HUD (`space_hud.ts`) are DOM, which an immersive session never
 * renders. A headset pilot had no cockpit, no speed, no hull and no target.
 *
 * WHAT IT DRAWS (all RIG children — the cockpit moves with the ship, so it is the pilot's REST FRAME):
 *  · THE FRAME — a canopy bow, two pillars, side sills and a dash ledge, thin and dark with a pale edge and a teal
 *    strip (the settler-tech palette), fixed around the head's REST pose (sampled, re-seated after a recenter). A static
 *    cockpit around the eyes lets more travel pass before discomfort (Cao, Jerald & Kopper 2018; Meta: cabins and
 *    cockpits "naturally obscure parts of the environment"; the "virtual nose"). The Sovereign has its REAL 3-D cabin —
 *    re-seated on the head instead, the procedural frame stays hidden.
 *  · THE HUD — one canvas quad on the dash (EVE Valkyrie: shield bars on the window, the target on a screen above the
 *    controls), cockpit-locked (Meta: head-locked is acceptable "for elements like gun reticules or cockpits"), at the
 *    comfort page's HUD size — independent of every other panel. It mirrors what the desktop HUD last SHOWED.
 *  · THE BORESIGHT — a fixed ring straight ahead (a rest-frame reticule: as good as a vignette in the racing-game
 *    comparison, with no information lost) — and THE TARGET RING on the line of sight from the HEAD to the target.
 * Inert outside a presenting session in the sector; `detach()` at the sector's deactivate.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { VrCanvasPanel } from './VrCanvasPanel.js';
import { col, colA, font, slab, text, bar, withAlpha } from './vr_draw.js';
import type { XRComfort, Hand } from '../../lib/xr_comfort.js';
import type { SpaceHudStatus } from '../../lib/space_hud.js';
import { spaceXrLegend, xrButtonFor } from '../../lib/space_xr_flight.js';

/** What SpaceModule hands the cockpit each frame (a plain view, no module reference). */
export interface SectorVrView {
  status: SpaceHudStatus | null;
  prompt: string | null;
  toast: { text: string; at: number } | null;
  /** The target: its WORLD position (render space), the IFF glyph + word, its label, and friend or foe. */
  target: { world: THREE.Vector3; glyph: string; word: string; label: string; friend: boolean } | null;
  /** The ship's roll from level (deg, + = left wing down), for the attitude line. */
  rollDeg: number;
  /** The chassis name (the HUD's title). */
  chassis: string;
  /** The Sovereign's real cabin (rig child) when this hull is the Sovereign — the frame stands down for it. */
  sovereignCabin: THREE.Object3D | null;
  /** That cabin's eye in rig space (where its builder seats the head). */
  sovereignEye: { y: number; z: number };
  /** The roll comfort in force, for the HUD line. */
  rollMode: string;
}

const HUD_W = 0.56, HUD_H = 0.28;      // metres at scale 1 (≈ 29° × 15° at the dash's 0.8 m)
const HUD_CW = 1024, HUD_CH = 512;
const HUD_OFF = new THREE.Vector3(0, -0.3, -0.78);
const HUD_TILT = -0.37;                 // rad about +X: the panel faces up toward the eye
const BORESIGHT_M = 30;
const TARGET_RING_M = 40;
const TARGET_RING_DEG = 2.2;
/** A head that stays this far from its rest for this long was re-centred (or the player moved): re-seat. */
const RESEAT_M = 0.6, RESEAT_S = 1.5;
const SAMPLE_FRAMES = 24;
const ALLOY = 0x2b2f36, EDGE = 0xc9c4b8, TEAL = 0x3fb8a8;

function basic(color: number, opts: { transparent?: boolean; opacity?: number; overlay?: boolean } = {}): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ color, transparent: !!opts.transparent, opacity: opts.opacity ?? 1, depthWrite: !opts.overlay, depthTest: !opts.overlay });
  m.fog = false;
  if (opts.transparent) markMrtOverlay(m); // THE OVERLAY LAW
  return m;
}

/** A thin bar between two rig-local points. */
function strut(a: THREE.Vector3, b: THREE.Vector3, r: number, mat: THREE.Material): THREE.Mesh {
  const len = a.distanceTo(b);
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 8, 1), mat);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3().subVectors(b, a).normalize());
  m.frustumCulled = false;
  return m;
}

/** The procedural rest frame, built about the eye at the origin (−Z ahead). */
export function buildRestFrame(): THREE.Group {
  const g = new THREE.Group();
  g.name = 'space-vr-frame';
  const alloy = basic(ALLOY), edge = basic(EDGE), teal = basic(TEAL);
  // The canopy bow: an arc over and ahead of the head.
  const bow = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.016, 6, 40, Math.PI * 0.86), edge);
  bow.position.set(0, -0.12, -0.62);
  bow.rotation.set(-0.42, 0, Math.PI * 0.07);
  bow.frustumCulled = false;
  g.add(bow);
  // The pillars: from the dash corners up to the bow's ends.
  g.add(strut(new THREE.Vector3(-0.56, -0.3, -0.64), new THREE.Vector3(-0.5, 0.26, -0.9), 0.022, alloy));
  g.add(strut(new THREE.Vector3(0.56, -0.3, -0.64), new THREE.Vector3(0.5, 0.26, -0.9), 0.022, alloy));
  // The side sills at elbow height (the periphery is where a rest frame counts most).
  g.add(strut(new THREE.Vector3(-0.62, -0.34, -0.66), new THREE.Vector3(-0.66, -0.36, 0.25), 0.03, alloy));
  g.add(strut(new THREE.Vector3(0.62, -0.34, -0.66), new THREE.Vector3(0.66, -0.36, 0.25), 0.03, alloy));
  // The dash ledge with its teal strip.
  const dash = new THREE.Mesh(new THREE.BoxGeometry(1.16, 0.035, 0.3), alloy);
  dash.position.set(0, -0.4, -0.66);
  dash.rotation.x = -0.18;
  dash.frustumCulled = false;
  g.add(dash);
  const strip = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.012, 0.012), teal);
  strip.position.set(0, -0.375, -0.81);
  strip.frustumCulled = false;
  g.add(strip);
  g.visible = false;
  return g;
}

export class SpaceVrCockpit {
  private frame: THREE.Group | null = null;
  private readonly hud = new VrCanvasPanel('space-vr-hud', HUD_W, HUD_H, HUD_CW, HUD_CH, 990);
  private readonly boresight: THREE.Mesh;
  private readonly ring: THREE.Mesh;
  private readonly ringMat: THREE.MeshBasicMaterial;
  private rig: THREE.Object3D | null = null;
  private rest: THREE.Vector3 | null = null;
  private readonly sampleSum = new THREE.Vector3();
  private sampleN = 0;
  private awayS = 0;
  private sovSeated: THREE.Object3D | null = null;
  private shownAt = 0;
  private readonly tv = new THREE.Vector3();
  private readonly tv2 = new THREE.Vector3();
  private readonly mInv = new THREE.Matrix4();
  readonly diag = { active: false, frame: false, sovereign: false, hud: false, rest: null as number[] | null, reseats: 0, paints: 0, ring: false, scale: 1 };

  constructor() {
    const bsMat = basic(0xffffff, { transparent: true, opacity: 0.55, overlay: true });
    this.boresight = new THREE.Mesh(new THREE.RingGeometry(0.17, 0.2, 40), bsMat);
    this.boresight.name = 'space-vr-boresight';
    this.boresight.renderOrder = 980;
    this.boresight.frustumCulled = false;
    this.boresight.visible = false;
    this.ringMat = basic(0xff5555, { transparent: true, opacity: 0.85, overlay: true });
    const r = TARGET_RING_M * Math.tan((TARGET_RING_DEG * Math.PI) / 180);
    this.ring = new THREE.Mesh(new THREE.RingGeometry(r * 0.86, r, 48), this.ringMat);
    this.ring.name = 'space-vr-target-ring';
    this.ring.renderOrder = 981;
    this.ring.frustumCulled = false;
    this.ring.visible = false;
  }

  /** One frame. `active` = presenting in the sector with the lever on; otherwise everything hides. */
  update(dt: number, active: boolean, rig: THREE.Object3D | null, cam: THREE.Object3D | null, c: XRComfort, v: SectorVrView): void {
    this.diag.active = active;
    this.moveHand = c.moveHand;
    if (!active || !rig || !cam) { this.hideAll(); return; }
    if (this.rig !== rig) this.attach(rig);
    if (!this.shownAt) this.shownAt = performance.now();
    // The head's REST: the average of the first frames' head positions (rig-local), then fixed; re-seated when the
    // head stays far from it (a recenter, or the player moved their chair).
    const head = cam.position;
    if (!this.rest) {
      this.sampleSum.add(head); this.sampleN++;
      if (this.sampleN >= SAMPLE_FRAMES) { this.rest = this.sampleSum.clone().divideScalar(this.sampleN); this.diag.reseats++; }
    } else {
      const away = Math.hypot(head.x - this.rest.x, head.y - this.rest.y, head.z - this.rest.z) > RESEAT_M;
      this.awayS = away ? this.awayS + Math.min(dt, 0.25) : 0;
      if (this.awayS > RESEAT_S) { this.reseat(); }
    }
    const rest = this.rest ?? head;
    this.diag.rest = [+rest.x.toFixed(3), +rest.y.toFixed(3), +rest.z.toFixed(3)];

    // The frame — or the Sovereign's own cabin seated on the head.
    const cabin = v.sovereignCabin;
    if (cabin) {
      cabin.position.set(rest.x, rest.y - v.sovereignEye.y, rest.z - v.sovereignEye.z);
      this.sovSeated = cabin;
      if (this.frame) this.frame.visible = false;
    } else {
      this.unseatSovereign();
      if (c.spaceFrame) {
        this.frame ??= buildRestFrame();
        if (this.frame.parent !== rig) rig.add(this.frame);
        this.frame.position.copy(rest);
        this.frame.visible = true;
      } else if (this.frame) this.frame.visible = false;
    }
    this.diag.frame = !!this.frame?.visible;
    this.diag.sovereign = !!cabin;

    // The HUD on the dash, at its own size.
    const s = Math.min(1.5, Math.max(0.75, c.spaceHudScale || 1));
    this.diag.scale = s;
    this.hud.mesh.scale.setScalar(s);
    this.hud.mesh.position.set(rest.x + HUD_OFF.x, rest.y + HUD_OFF.y - 0.07 * (s - 1), rest.z + HUD_OFF.z);
    this.hud.mesh.rotation.set(HUD_TILT, 0, 0);
    this.hud.visible = true;
    this.diag.hud = true;
    this.paintHud(v);
    this.diag.paints = this.hud.paints;

    // The boresight, straight ahead of the eye (parallel to the bore, as the desktop reticle is).
    this.boresight.position.set(rest.x, rest.y, rest.z - BORESIGHT_M);
    this.boresight.visible = true;

    // The target ring on the HEAD's line of sight (never the rig origin's — 1.6 m of parallax at 40 m is 2°).
    const t = v.target;
    let ringOn = false;
    if (t) {
      rig.updateWorldMatrix(true, false);
      this.mInv.copy(rig.matrixWorld).invert();
      const local = this.tv.copy(t.world).applyMatrix4(this.mInv);           // the target in rig space
      const dir = this.tv2.copy(local).sub(head);
      const d = dir.length();
      if (d > 1 && dir.z / d < 0.2) {                                       // ahead-ish of the cockpit, never behind you
        dir.multiplyScalar(TARGET_RING_M / d);
        this.ring.position.copy(head).add(dir);
        this.ring.lookAt(this.tv.copy(head).applyMatrix4(rig.matrixWorld));  // lookAt takes a WORLD point
        this.ringMat.color.setHex(t.friend ? 0x5cff9d : 0xff5555);
        ringOn = true;
      }
    }
    this.ring.visible = ringOn;
    this.diag.ring = ringOn;
  }

  private attach(rig: THREE.Object3D): void {
    this.detach();
    this.rig = rig;
    rig.add(this.hud.mesh, this.boresight, this.ring);
    this.rest = null; this.sampleSum.set(0, 0, 0); this.sampleN = 0; this.awayS = 0;
  }

  /** Forget the rest pose and sample it again (a recenter; the comfort page's seated / height change). */
  reseat(): void { this.rest = null; this.sampleSum.set(0, 0, 0); this.sampleN = 0; this.awayS = 0; }

  private unseatSovereign(): void {
    if (this.sovSeated) { this.sovSeated.position.set(0, 0, 0); this.sovSeated = null; }
  }

  private hideAll(): void {
    this.unseatSovereign();
    if (this.frame) this.frame.visible = false;
    this.hud.visible = false;
    this.boresight.visible = false;
    this.ring.visible = false;
    this.diag.frame = false; this.diag.hud = false; this.diag.ring = false;
    this.shownAt = 0;
  }

  /** The sector is gone (deactivate): off the rig, the rest forgotten. */
  detach(): void {
    this.hideAll();
    this.frame?.removeFromParent();
    this.hud.mesh.removeFromParent();
    this.boresight.removeFromParent();
    this.ring.removeFromParent();
    this.rig = null;
    this.reseat();
  }

  private paintHud(v: SectorVrView): void {
    const st = v.status;
    const toastLive = v.toast && Date.now() - v.toast.at < 5000 ? v.toast.text : '';
    const legendOn = performance.now() - this.shownAt < 45_000;
    const r = (n: number, q = 1): number => Math.round(n / q) * q;
    const sig = st
      ? `${r(st.speed)}|${r(st.throttle * 100)}|${r(st.hull / Math.max(1, st.hullMax) * 50)}|${r(st.shieldF / Math.max(1, st.shieldMax) * 25)}|${r(st.shieldR / Math.max(1, st.shieldMax) * 25)}|`
        + `${r(st.cap / Math.max(1, st.capMax) * 25)}|${r(st.booster / Math.max(1, st.boosterMax) * 25)}|${st.boost}|${r(st.cruise, 0.5)}|${st.matchSpeed}|`
        + `${st.target ? `${st.target.name}|${r(st.target.dist, 50)}|${st.target.hp}` : '-'}|${st.nav ? `${st.nav.name}|${r(st.nav.dist, 1000)}|${st.nav.state}` : '-'}`
      : 'none';
    const tag = v.target ? `${v.target.glyph}${v.target.word}` : '';
    this.hud.paint(`${sig}|${tag}|${v.prompt ?? ''}|${toastLive}|${r(v.rollDeg, 5)}|${v.rollMode}|${legendOn}|${v.chassis}|${this.moveHand}`, (g) => this.drawHud(g, v, toastLive, legendOn));
  }

  private drawHud(g: CanvasRenderingContext2D, v: SectorVrView, toast: string, legend: boolean): void {
    const W = HUD_CW, H = HUD_CH;
    slab(g, { x: 6, y: 6, w: W - 12, h: H - 12 }, 22, colA('--ax-bg', 0.82, '#120d0a'), col('--ax-line2', '#6b5a48'), 3);
    const st = v.status;
    const accent = col('--ax-accent', '#d9a441');
    const txt = col('--ax-text', '#efe6d2');
    const soft = col('--ax-soft', '#cdbfa6');
    const muted = col('--ax-muted', '#8c7a66');
    const track = colA('--ax-text', 0.12, '#efe6d2');
    text(g, v.chassis.toUpperCase(), 30, 50, muted, font(24, 'mono', 600), 300);
    if (!st) { text(g, 'Flight systems coming online…', 30, 120, soft, font(34)); return; }
    // Speed + throttle (left).
    text(g, `${Math.round(st.speed)} m/s`, 30, 112, txt, font(58, 'mono', 700), 330);
    const tags = `${st.boost ? 'BOOST  ' : ''}${st.cruise > 1.15 ? `CRUISE ×${st.cruise.toFixed(1)}  ` : ''}${st.matchSpeed ? 'MATCH' : ''}`;
    text(g, `THR ${Math.round(st.throttle * 100)}%`, 30, 158, soft, font(30, 'mono', 600), 170);
    bar(g, { x: 200, y: 138, w: 150, h: 18 }, st.throttle, accent, track);
    if (tags) text(g, tags, 30, 198, accent, font(26, 'mono', 700), 330);
    // Hull / shields / capacitor / boost (right).
    const rows: Array<[string, number, string]> = [
      ['HULL', st.hull / Math.max(1, st.hullMax), st.hull / Math.max(1, st.hullMax) < 0.3 ? col('--ax-bad', '#e0604a') : col('--ax-good', '#7fc97f')],
      ['F SHLD', st.shieldF / Math.max(1, st.shieldMax), col('--ax-info', '#6db3f2')],
      ['R SHLD', st.shieldR / Math.max(1, st.shieldMax), col('--ax-info', '#6db3f2')],
      ['CAP', st.cap / Math.max(1, st.capMax), accent],
      ['BOOST', st.booster / Math.max(1, st.boosterMax), col('--ax-rare', '#b48ae0')],
    ];
    rows.forEach(([label, f, c], i) => {
      const y = 62 + i * 34;
      text(g, label, 400, y + 20, soft, font(24, 'mono', 600), 110);
      bar(g, { x: 512, y: y + 4, w: 380, h: 20 }, f, c, track);
      text(g, `${Math.round(Math.max(0, Math.min(1, f)) * 100)}%`, 990, y + 21, txt, font(22, 'mono', 600), 90, 'right');
    });
    // The target: glyph + WORD + label (never colour alone), range and hull.
    const ty = 262;
    if (st.target && v.target) {
      const tCol = v.target.friend ? col('--ax-good', '#7fc97f') : col('--ax-bad', '#e0604a');
      const range = st.target.dist >= 1000 ? `${(st.target.dist / 1000).toFixed(1)} km` : `${Math.round(st.target.dist)} m`;
      text(g, `${v.target.glyph} ${v.target.word.toUpperCase()}`, 30, ty, tCol, font(30, 'body', 700), 220);
      text(g, `${v.target.label} · ${range}`, 250, ty, txt, font(30, 'body', 600), 560);
      bar(g, { x: 820, y: ty - 22, w: 170, h: 20 }, st.target.hp / Math.max(1, st.target.hpMax), tCol, track);
    } else {
      text(g, `No target — ${xrButtonFor('spaceTarget')} cycles the nearest threat`, 30, ty, muted, font(28), 960);
    }
    // The prompt / campaign line and the last toast.
    const lines: string[] = [];
    if (v.prompt) lines.push(v.prompt);
    if (toast) lines.push(toast);
    lines.slice(0, 2).forEach((l, i) => text(g, l, 30, 312 + i * 40, i === 0 && v.prompt ? soft : accent, font(26), 964));
    // Attitude + the roll comfort in force.
    const roll = Math.round(v.rollDeg);
    text(g, `roll ${roll === 0 ? 'level' : `${Math.abs(roll)}° ${roll > 0 ? 'left' : 'right'}`} · ${v.rollMode}`, 30, 400, muted, font(24, 'mono', 500), 600);
    // The controller legend for the first moments (then a compact reminder).
    const leg = legend ? spaceXrLegend(this.moveHand) : [`${xrButtonFor('spaceMissile')} missile · ${xrButtonFor('spaceTarget')} target · A level · X menu`];
    leg.forEach((l, i) => text(g, l, 30, (legend ? 432 : 468) + i * 28, withAlpha(soft, 0.9), font(22), 964));
  }

  /** The comfort page's throttle hand (the legend's words follow it). */
  private moveHand: Hand = 'left';

  state(): object { return { ...this.diag, hudPaints: this.hud.paints }; }
}
